import React, { ReactElement, ReactNode } from "react";
import styled from "styled-components";
import { Colors } from "../../../utils/colors";
import Typography from "../../typography";
import Sparkle from "../../../images/png/sparkle.png";
import Spinner from "components/spinner";

interface IProps extends React.HTMLProps<HTMLButtonElement> {
  text?: string;
  textSize?: string;
  onClick?: () => void;
  variant?:
    | "white"
    | "danger"
    | "success"
    | "p-fade"
    | "gray"
    | "d-fade"
    | "text"
    | "border";
  sparkle?: boolean;
  loading?: boolean;
  textColor?: string;
  icon?: ReactNode;
  iconPosition?: "left" | "right" | "center";
  m_iconPosition?: "left" | "right" | "center";
  inset?: boolean;
  iconSize?: string;
  fitContent?: boolean;
  m_iconButton?: boolean;
  rounded?: boolean;
}
const PrimaryButton = ({
  text = "Button",
  sparkle = false,
  textSize = "m",
  variant,
  onClick = () => {},
  loading,
  disabled,
  iconPosition = "left",
  icon,
  inset = false,
  iconSize,
  textColor = "",
  fitContent = false,
  m_iconButton = false,
  rounded = false,
  ...rest
}: IProps) => {
  return (
    <Container
      onClick={() => onClick()}
      {...rest}
      disabled={loading || disabled}
      variant={variant}
      inset={inset ? "true" : "false"}
      textcolor={textColor}
      fitcontent={fitContent ? "true" : "false"}
      rounded={rounded ? "true" : "false"}
    >
      {loading ? (
        <Spinner />
      ) : (
        <>
          {iconPosition === "left" && icon && (
            <IconContainer
              m_iconbutton={m_iconButton ? "true" : "false"}
              position={iconPosition}
              width={iconSize}
            >
              {icon}
            </IconContainer>
          )}

          {iconPosition === "center" && icon ? (
            <IconContainer
              m_iconbutton={m_iconButton ? "true" : "false"}
              position={iconPosition}
              width={iconSize}
            >
              {icon}
            </IconContainer>
          ) : (
            <Typography
              weight="medium"
              color={textColor ? textColor : "inherit"}
              m_display={m_iconButton ? "none" : "unset"}
              size={textSize}
            >
              {text}
            </Typography>
          )}
          {iconPosition === "right" && icon && (
            <IconContainer
              m_iconbutton={m_iconButton ? "true" : "false"}
              position={iconPosition}
              width={iconSize}
            >
              {icon}
            </IconContainer>
          )}
        </>
      )}

      {sparkle && !disabled && <SparkleContainer src={Sparkle} alt="sparkle" />}
    </Container>
  );
};

export default PrimaryButton;

interface IButtonProps extends React.HTMLProps<HTMLButtonElement> {
  variant?: string;
  danger?: string;
  inset?: string;
  textcolor?: string;
  fitcontent?: string;
  rounded?: string;
}
const Container = styled.button<IButtonProps>`
  width: ${(props) => (props.fitcontent === "true" ? "fit-content" : "100%")};

  background-color: ${(props) =>
    props.variant === "p-fade"
      ? "rgba(254, 161, 1, 0.10)"
      : props.variant === "white"
      ? Colors.white100
      : props.variant === "gray"
      ? Colors.grey600
      : props.variant === "danger"
      ? Colors.danger
      : props.variant === "success"
      ? Colors.success
      : props.variant === "d-fade"
      ? "rgba(255, 51, 51, 0.10)"
      : props.variant === "text" || props.variant === "border"
      ? "transparent"
      : Colors.primary};

  padding: ${(props) => (props.variant === "text" ? "0" : "1.1rem 1.6rem")};
  color: ${(props) =>
    props.textcolor
      ? props.textcolor
      : props.variant === "p-fade"
      ? Colors.yellow500
      : props.variant === "white"
      ? Colors.grey900
      : props.variant === "gray"
      ? Colors.white100
      : props.variant === "danger"
      ? Colors.white100
      : props.variant === "success"
      ? Colors.white100
      : props.variant === "d-fade"
      ? Colors.danger
      : props.variant === "text"
      ? Colors.yellow500
      : Colors.grey900};
  border-radius: ${(props) => (props.rounded === "true" ? "50%" : "0.8rem")};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: 1px solid
    ${(props) =>
      props.variant === "border"
        ? props.textcolor
        : props.variant === "p-fade"
        ? "transparent"
        : props.variant === "gray"
        ? "#505050"
        : props.variant === "white"
        ? "transparent"
        : props.variant === "d-fade" || "text"
        ? "transparent"
        : "#664100"};

  position: relative;

  box-shadow: ${(props) =>
    props.inset === "true" && !props.disabled
      ? props.variant === "white"
        ? `-4px 0px 4px 0px rgba(252, 252, 252, 0.80) inset, 0px 4px 4px 0px rgba(255, 255, 255, 0.80) inset`
        : props.variant === "danger"
        ? Colors.white100
        : props.variant === "gray"
        ? "-4px 0px 4px 0px rgba(98, 98, 98, 0.80) inset, 0px 4px 4px 0px rgba(59, 59, 59, 0.80) inset"
        : props.variant === "success"
        ? Colors.white100
        : `-4px 0px 4px 0px rgba(254, 198, 103, 0.8) inset,
    0px 4px 4px 0px rgba(254, 198, 103, 0.8) inset`
      : ""};

  &:hover {
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    cursor: not-allowed;
    background-color: ${(props) => Colors.grey600};
    color: ${(props) => Colors.grey500};
    transform: none;
  }

  @media only screen and (max-width: 769px) {
    padding: ${(props) => (props.variant === "text" ? "0" : "0.8rem")};
  }

  transition: all 0.2s ease-in-out;
`;

const SparkleContainer = styled.img`
  position: absolute;
  right: 0.8rem;
  width: 2.1rem;
  height: 2.2rem;
`;

const IconContainer = styled.div<{
  position: string;
  width?: string;
  m_iconbutton?: string;
}>`
  margin-left: ${(props) => (props.position === "right" ? "1rem" : "0")};
  margin-right: ${(props) => (props.position === "left" ? "1rem" : "0")};
  width: ${(props) => `${props.width}rem`};
  height: ${(props) => `${props.width}rem`};
  display: flex;
  justify-content: center;
  align-items: center;

  @media only screen and (max-width: 769px) {
    margin: ${(props) => (props.m_iconbutton === "true" ? "0rem" : "")};
  }
`;
